import { Box, Checkbox as MuiCheckbox, FormControlLabel, Switch as MuiSwitch } from '@mui/material';
import { Check, Minus } from 'lucide-react';
import type { ReactElement } from 'react';

import { Icon } from './Icon';

/**
 * Interrupteur et case a cocher (maquettes « BO/Interrupteur », « BO/Case »).
 *
 * Deux controles, deux sens : l'**interrupteur** applique un etat tout de suite
 * (une bascule de fonctionnalite), la **case** prepare une selection qu'un
 * bouton validera (des lignes de tableau, un consentement). Les confondre fait
 * croire qu'un reglage est enregistre alors qu'il attend une validation.
 *
 * Les deux sont des `input` natifs habilles par MUI : clavier (Espace),
 * annonce de l'etat et focus visible viennent de la plateforme. Un libelle est
 * **obligatoire** ; `hideLabel` le retire de l'ecran — quand la rangee ecrit
 * deja le texte a cote — mais jamais de l'arbre d'accessibilite.
 *
 * Contours : la case vide et la piste eteinte sont en `textMuted`, et non en
 * `border`, parce qu'ils **identifient** le controle et doivent tenir 3:1
 * (WCAG 1.4.11, `tokens.contrast.test.ts`) ; `border` n'est qu'un separateur.
 */

interface ChoiceProps {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  readonly hideLabel?: boolean;
}

const withLabel = (control: ReactElement, label: string, hideLabel: boolean, disabled: boolean): ReactElement =>
  hideLabel ? (
    control
  ) : (
    <FormControlLabel
      control={control}
      label={label}
      disabled={disabled}
      sx={{ mx: 0, gap: 1, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
    />
  );

export const Switch = ({ label, checked, onChange, disabled = false, hideLabel = false }: ChoiceProps): ReactElement =>
  withLabel(
    <MuiSwitch
      checked={checked}
      onChange={(_event, value) => {
        onChange(value);
      }}
      disabled={disabled}
      slotProps={{ input: { 'aria-label': hideLabel ? label : undefined } }}
      sx={{
        width: 40,
        height: 22,
        p: 0,
        overflow: 'visible',
        '& .MuiSwitch-switchBase': {
          p: '2px',
          color: 'tokens.surface',
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: 'tokens.surface',
            '& + .MuiSwitch-track': { bgcolor: 'tokens.primary', opacity: 1 },
          },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.4 },
        },
        '& .MuiSwitch-thumb': { width: 18, height: 18, boxShadow: 'none' },
        '& .MuiSwitch-track': { borderRadius: 11, bgcolor: 'tokens.textMuted', opacity: 1 },
      }}
    />,
    label,
    hideLabel,
    disabled,
  );

const BOX = 18;

const boxSx = {
  width: BOX,
  height: BOX,
  borderRadius: '5px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const EmptyBox = (): ReactElement => (
  <Box component="span" sx={{ ...boxSx, border: '1.5px solid', borderColor: 'tokens.textMuted', bgcolor: 'tokens.surface' }} />
);

const FilledBox = ({ mark }: { mark: typeof Check }): ReactElement => (
  <Box component="span" sx={{ ...boxSx, bgcolor: 'tokens.primary', color: 'tokens.textOnPrimary' }}>
    <Icon icon={mark} size={14} />
  </Box>
);

export interface CheckboxProps extends ChoiceProps {
  /**
   * Selection partielle, pour la case d'en-tete d'un tableau dont une partie des
   * lignes est cochee. Annoncee « mixte » par les lecteurs d'ecran.
   */
  readonly indeterminate?: boolean;
}

export const Checkbox = ({
  label,
  checked,
  onChange,
  disabled = false,
  hideLabel = false,
  indeterminate = false,
}: CheckboxProps): ReactElement =>
  withLabel(
    <MuiCheckbox
      checked={checked}
      indeterminate={indeterminate}
      onChange={(_event, value) => {
        onChange(value);
      }}
      disabled={disabled}
      icon={<EmptyBox />}
      checkedIcon={<FilledBox mark={Check} />}
      indeterminateIcon={<FilledBox mark={Minus} />}
      // MUI ne pose qu'un attribut `data-indeterminate` : sans la propriete DOM,
      // la case serait annoncee « non cochee » au lieu de « mixte ».
      slotProps={{
        input: {
          'aria-label': hideLabel ? label : undefined,
          ref: (input: HTMLInputElement | null) => {
            if (input !== null) {
              input.indeterminate = indeterminate;
            }
          },
        },
      }}
      sx={{ '&.Mui-disabled': { opacity: 0.5 } }}
    />,
    label,
    hideLabel,
    disabled,
  );
